import { format } from 'date-fns'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { pharmacyCountAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import SankeyDiagram from '../components/SankeyDiagram'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge
} from '../components/ui'

function formatNumber(v) {
  return (v || 0).toLocaleString()
}

export default function PharmacyCountPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { clinics } = useAuthStore()

  const [dateRange, setDateRange] = useState(() => {
    const s = searchParams.get('startDate')
    const e = searchParams.get('endDate')
    if (s && e) return { from: new Date(s), to: new Date(e) }
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    return { from: start, to: end }
  })

  const [filterClinicId, setFilterClinicId] = useState(searchParams.get('clinic') || '')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      }
      if (filterClinicId) params.clinicId = filterClinicId
      const res = await pharmacyCountAPI.getReport(params)
      if (res.data.success) {
        setData(res.data.data)
      } else {
        throw new Error(res.data.error || 'Failed to load')
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, filterClinicId])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set('startDate', format(dateRange.from, 'yyyy-MM-dd'))
    params.set('endDate', format(dateRange.to, 'yyyy-MM-dd'))
    if (filterClinicId) params.set('clinic', filterClinicId)
    setSearchParams(params, { replace: true })
  }, [dateRange, filterClinicId])

  // Preprocess sankey data to set node colors
  const sankeyData = data?.sankey ? {
    ...data.sankey,
    nodes: data.sankey.nodes.map(n => ({
      ...n,
      // Map group to SankeyDiagram's understood groups for color
      group: n.group,
    })),
  } : null

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Pharmacy Count</h1>
          <p className="text-muted-foreground mt-1">
            Order routing across clinics, pharmacies, and fulfillment status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterClinicId}
            onChange={(e) => setFilterClinicId(e.target.value)}
            className="text-sm border rounded-md px-3 py-2 bg-background text-foreground"
          >
            <option value="">All Clinics</option>
            {clinics?.map(c => (
              <option key={c.id} value={c.id}>{c.displayName || c.name || c.id}</option>
            ))}
          </select>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {data?.errors?.length > 0 && (
        <Card className="border-yellow-500">
          <CardContent className="pt-6">
            <p className="text-yellow-600 text-sm">
              Some clinics failed to load: {data.errors.map(e => e.clinicId).join(', ')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{formatNumber(data.summary.total_orders)}</span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Clinics</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{data.summary.clinics}</span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pharmacies</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{data.summary.pharmacies}</span>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sankey Diagram */}
      <Card>
        <CardHeader>
          <CardTitle>Order Flow</CardTitle>
          <CardDescription>Clinic &rarr; Pharmacy</CardDescription>
        </CardHeader>
        <CardContent>
          <SankeyDiagram
            data={sankeyData}
            type="pharmacyCount"
            height={Math.max(500, (data?.summary?.pharmacies || 5) * 40)}
            loading={loading}
            emptyMessage="No orders found for the selected date range"
          />
        </CardContent>
      </Card>

      {/* Pharmacy Breakdown Table */}
      {data?.table?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pharmacy Breakdown</CardTitle>
            <CardDescription>Orders per pharmacy by clinic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Clinic</th>
                    <th className="text-left py-2 px-3 font-medium">Pharmacy</th>
                    <th className="text-right py-2 px-3 font-medium">Orders</th>
                    <th className="text-right py-2 px-3 font-medium">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.table.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-3">{row.clinic}</td>
                      <td className="py-2 px-3 font-medium">{row.pharmacy}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatNumber(row.count)}</td>
                      <td className="py-2 px-3 text-right">
                        <Badge variant="secondary" className="text-xs">
                          {data.summary.total_orders > 0
                            ? ((row.count / data.summary.total_orders) * 100).toFixed(1)
                            : 0}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
